/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, memo, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import GroupProperties, {getAssetCategories} from '@ion/core/src/services/GroupProperties';
import FilterEmptyState from '@ion/dls/src/filters/FilterEmptyState';
import {createFilterOptions} from '@ion/dls/src/filters/FilterHelpers';
import changePage, {Route} from '@/routes/RouteDefinitions';
import {setCategoryFilters} from '@/store/actions/tracking';
import {globalGroupIdSelector} from '@/store/selectors/global-group-chooser-selectors';
import {trackingFiltersSelector} from '@/store/selectors/tracking-selectors';
import TrackingDrawerFilter, {
  TrackingDrawerFilterHandle,
  TrackingDrawerFilterOption
} from './TrackingDrawerFilter';
import {TrackingFilterNavigationRequest, TrackingFilterStateKey} from './types';
import {requestTrackingFilterNavigation} from './utils';

export type TrackingAssetCategoryFilterHandle = TrackingDrawerFilterHandle;

export interface TrackingAssetCategoryFilterProps {
  onRequestNavigate?: TrackingFilterNavigationRequest;
  onSelectedCountChange?: (count: number) => void;
  onSelectionChange?: () => void;
}

type FilterHandle = TrackingAssetCategoryFilterHandle;
type FilterProps = TrackingAssetCategoryFilterProps;

export const TrackingAssetCategoryFilter = forwardRef<FilterHandle, FilterProps>(({
  onRequestNavigate,
  onSelectedCountChange,
  onSelectionChange
}, ref) => {
  const globalGroupId           = useSelector(globalGroupIdSelector);
  const trackingFilters         = useSelector(trackingFiltersSelector);
  const selectedAssetCategories = trackingFilters?.assetCategories?.selected ?? [];

  const loadOptions = useCallback(async(): Promise<TrackingDrawerFilterOption[]> => {
    if (!globalGroupId) {
      return [];
    }
    await GroupProperties.getInheritedProperties(globalGroupId);
    const assetCategories = await getAssetCategories();
    return createFilterOptions(assetCategories as any);
  }, [globalGroupId]);

  const handleManageCategories = useCallback(function() {
    requestTrackingFilterNavigation(function() {
      changePage(Route.ADMIN_GROUPS);
    }, onRequestNavigate);
  }, [onRequestNavigate]);

  return (
    <TrackingDrawerFilter ref={ref}
      applyFilter={setCategoryFilters}
      filterStateKey={TrackingFilterStateKey.AssetCategories}
      emptyState={(
        <FilterEmptyState title={translate('filters:tracking.assetCategory.empty.title')}
          description={translate('filters:tracking.assetCategory.empty.description')}
          actionLabel={translate('filters:tracking.assetCategory.empty.action')}
          onAction={handleManageCategories} />
      )}
      loadOptions={loadOptions}
      selectedItems={selectedAssetCategories}
      searchPlaceholder={translate('filters:tracking.assetCategory.searchPlaceholder')}
      onSelectedCountChange={onSelectedCountChange}
      onSelectionChange={onSelectionChange} />
  );
});

export default memo(TrackingAssetCategoryFilter);
